
import { Link } from 'react-router-dom';

export default function ElderlyMedicationManagementPage() {
  // 服薬管理のコツデータ
  const managementTips = [
    {
      icon: 'ri-calendar-check-line',
      title: 'お薬カレンダーの活用',
      description: '1週間分の薬を曜日別・時間別に整理できる薬入れです。透明なポケットに薬を入れることで、飲み忘れや重複服用を防ぐことができます。',
      benefits: [
        '飲み忘れの防止',
        '重複服用の回避',
        '視覚的な確認が可能',
        '家族による管理サポート'
      ],
      usage: '毎週決まった曜日に1週間分の薬をセットし、服薬後は空になったポケットで確認します。'
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: '一包化サービス',
      description: '薬局で複数の薬を1回分ずつ包装してもらうサービスです。服薬時間ごとに分けられるため、飲み間違いを防げます。',
      benefits: [
        '飲み間違いの防止',
        '服薬の簡素化',
        '薬の識別が容易',
        '持ち運びが便利'
      ],
      usage: '薬剤師に相談して一包化を依頼し、包装に記載された時間通りに服薬します。'
    },
    {
      icon: 'ri-alarm-line',
      title: 'アラーム・リマインダー',
      description: 'スマートフォンや専用機器のアラーム機能を使って、服薬時間をお知らせします。習慣化に効果的です。',
      benefits: [
        '服薬時間の正確性',
        '習慣化の促進',
        '忘れにくい仕組み',
        '家族への通知も可能'
      ],
      usage: '毎日同じ時間にアラームを設定し、鳴ったらすぐに服薬する習慣をつけます。'
    },
    {
      icon: 'ri-file-list-3-line',
      title: '服薬記録の作成',
      description: '服薬した日時や体調の変化を記録することで、薬の効果や副作用を把握できます。医師との相談にも役立ちます。',
      benefits: [
        '服薬状況の把握',
        '副作用の早期発見',
        '医師との情報共有',
        '治療効果の確認'
      ],
      usage: '服薬カレンダーや手帳に服薬時間と体調を記録し、定期的に見直します。'
    }
  ];

  // よくある問題と対策
  const commonProblems = [
    {
      problem: '飲み忘れが頻繁に起こる',
      causes: [
        '服薬時間が不規則',
        '薬の種類が多すぎる',
        '記憶力の低下',
        '生活リズムの変化'
      ],
      solutions: [
        'アラーム機能の活用',
        'お薬カレンダーの使用',
        '家族による声かけ',
        '服薬時間の統一'
      ],
      prevention: '毎日同じ時間に服薬する習慣をつけ、目につく場所に薬を置くことが効果的です。'
    },
    {
      problem: '薬を重複して飲んでしまう',
      causes: [
        '服薬したかどうか忘れる',
        '薬の管理が不十分',
        '複数の薬が混在',
        '認知機能の低下'
      ],
      solutions: [
        '一包化サービスの利用',
        '服薬記録の作成',
        '薬の整理整頓',
        '家族による管理サポート'
      ],
      prevention: '服薬後は必ず記録をつけ、薬を決まった場所に保管することが重要です。'
    },
    {
      problem: '薬の効果が感じられない',
      causes: [
        '服薬方法の間違い',
        '食事との相互作用',
        '服薬時間の不適切',
        '薬の保存状態が悪い'
      ],
      solutions: [
        '薬剤師への相談',
        '服薬方法の確認',
        '食事時間の調整',
        '適切な保存方法の実践'
      ],
      prevention: '薬剤師の指示通りに服薬し、定期的に効果を確認することが大切です。'
    }
  ];

  // お薬カレンダーの使い方
  const medicationCalendarTips = [
    {
      step: '薬の整理と確認',
      description: '処方された薬をすべて確認し、服薬時間ごとに分類します。',
      details: [
        '朝・昼・夕・寝る前の4つの時間帯に分ける',
        '薬の名前と用法・用量を確認',
        '期限切れの薬がないかチェック'
      ]
    },
    {
      step: 'カレンダーへのセット',
      description: '1週間分の薬を曜日別・時間別のポケットに入れます。',
      details: [
        '月曜日から日曜日まで順番にセット',
        '各時間帯のポケットに正確に配置',
        'セット後は薬の数を再確認'
      ]
    },
    {
      step: '服薬と確認',
      description: '決められた時間に薬を取り出して服薬し、空のポケットで確認します。',
      details: [
        'アラームが鳴ったらすぐに服薬',
        '空になったポケットで服薬済みを確認',
        '飲み忘れがあった場合の対処法を確認'
      ]
    },
    {
      step: '週次の見直し',
      description: '1週間の服薬状況を振り返り、問題があれば改善策を検討します。',
      details: [
        '飲み忘れの回数と原因を分析',
        '生活リズムとの適合性を確認',
        '必要に応じて薬剤師に相談'
      ]
    }
  ];

  // 注意すべき症状
  const warningSignals = [
    'めまいやふらつきが続く',
    '食欲不振や吐き気が続く',
    '皮膚に発疹や赤みが出る',
    '呼吸困難や胸の痛み',
    '意識がもうろうとする',
    '手足のしびれや震え',
    '異常な眠気や不眠',
    '血圧や血糖値の急激な変化'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/home" className="flex items-center">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-capsule-line text-lg"></i>
                </div>
                <span className="text-xl font-bold text-gray-900">ファーマシー</span>
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/home" className="text-gray-600 hover:text-teal-600 transition-colors">ホーム</a>
              <a href="/services" className="text-gray-600 hover:text-teal-600 transition-colors">サービス</a>
              <a href="/beast-blog" className="text-teal-600 font-medium">ビースト君ブログ</a>
              <a href="/health-consultation" className="text-gray-600 hover:text-teal-600 transition-colors">健康相談</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Simplified */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-user-heart-line text-2xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            高齢者の飲み忘れ対策
          </h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="bg-gray-50 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-teal-600 cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/beast-blog" className="text-gray-500 hover:text-teal-600 cursor-pointer">ビースト君ブログ</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">高齢者の飲み忘れ対策</span>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
              <img 
                src="https://public.readdy.ai/ai/img_res/dbe372861181c69374dfc05760b8ebc3.jpg"
                alt="ビースト君"
                className="w-14 h-14 object-cover object-top rounded-full border-2 border-teal-300"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="bg-orange-100 text-orange-800 text-sm px-3 py-1 rounded-full font-medium">高齢者ケア</span>
                <span className="bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full font-medium">NEW</span>
                <span className="text-gray-500 text-sm">2025年10月10日</span>
              </div>
              <p className="text-gray-600">ビースト君 • 薬剤師</p>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            高齢者の飲み忘れ対策｜薬剤師がすすめる服薬管理のコツ
          </h1>
          
          <div className="relative mb-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20in%20modern%20pharmacy%20consulting%20with%20elderly%20patient%20about%20medication%20management%2C%20showing%20pill%20organizer%20calendar%20and%20medication%20schedule%20charts%2C%20professional%20healthcare%20setting%20with%20various%20medication%20management%20tools%20displayed%2C%20friendly%20cartoon%20style%20with%20detailed%20pharmacy%20background%20and%20elderly%20care%20focus&width=800&height=400&seq=beast-elderly-medication-hero&orientation=landscape"
              alt="高齢者の服薬管理について説明するビースト君"
              className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-600 leading-relaxed">
              こんにちは、ビースト君です！💪 今日は高齢者の方々が抱える「薬の飲み忘れ」問題について詳しく解説します。
              年齢を重ねると複数の薬を服用することが多くなり、管理が複雑になりがちです。
              薬剤師として多くの高齢者の方々をサポートしてきた経験から、効果的な服薬管理のコツをお伝えします。
              安全で確実な服薬管理で、健康な毎日を送りましょう！
            </p>
          </div>
        </header>

        {/* Introduction */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">高齢者の服薬管理が重要な理由</h2>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 mb-8">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-user-heart-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">高齢者特有の課題</h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>多剤併用：</strong>複数の慢性疾患により多くの薬を服用</p>
                  <p><strong>記憶力の変化：</strong>服薬時間や服薬の有無を忘れやすい</p>
                  <p><strong>視力・聴力の低下：</strong>薬の識別や指示の理解が困難</p>
                  <p><strong>手指の機能低下：</strong>薬の包装を開けるのが困難</p>
                  <p><strong>副作用のリスク：</strong>加齢により薬の代謝・排泄機能が低下</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            高齢者の服薬管理は、単に薬を飲むだけでなく、安全性と効果を両立させる重要な課題です。
            適切な管理により、治療効果を最大化し、副作用や薬物相互作用のリスクを最小限に抑えることができます。
          </p>
        </section>

        {/* Management Tips */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">効果的な服薬管理方法</h2>
          
          <div className="space-y-8">
            {managementTips.map((tip, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                    <i className={`${tip.icon} text-xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-teal-600 mb-4">{tip.title}</h3>
                    <p className="text-gray-700 mb-6">{tip.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">メリット</h4>
                        <ul className="space-y-2">
                          {tip.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">使用方法</h4>
                        <p className="text-gray-700">{tip.usage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Common Problems */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">よくある問題と対策</h2>
          
          <div className="grid gap-6">
            {commonProblems.map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-error-warning-line"></i>
                  </div>
                  <h3 className="text-xl font-bold text-red-600">{item.problem}</h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">主な原因</h4>
                    <ul className="space-y-1">
                      {item.causes.map((cause, idx) => (
                        <li key={idx} className="text-gray-700 text-sm">• {cause}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">対策方法</h4>
                    <ul className="space-y-1">
                      {item.solutions.map((solution, idx) => (
                        <li key={idx} className="text-gray-700 text-sm">• {solution}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">予防策</h4>
                    <p className="text-gray-700 text-sm">{item.prevention}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Medication Calendar Guide */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">お薬カレンダーの使い方</h2>
          
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-8 mb-8">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-calendar-check-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">お薬カレンダーとは</h3>
                <p className="text-gray-700">
                  1週間分の薬を曜日別・時間別に整理できる薬入れです。
                  透明なポケットに薬を入れることで、飲み忘れや重複服用を防ぐことができます。
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              {medicationCalendarTips.map((step, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{step.step}</h4>
                    <p className="text-gray-700 mb-3">{step.description}</p>
                    <ul className="space-y-1">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="text-gray-600 text-sm">• {detail}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Warning Signals */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">注意すべき症状</h2>
          
          <div className="bg-red-50 border border-red-200 rounded-xl p-8">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-alarm-warning-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-800 mb-3">すぐに医師・薬剤師に相談すべき症状</h3>
                <p className="text-red-700">以下の症状が見られた場合は、服薬管理に問題がある可能性があります。</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {warningSignals.map((signal, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-red-700">{signal}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Beast-kun's Experience */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-8">
            <div className="flex items-start">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                <img 
                  src="https://readdy.ai/api/search-image?query=Friendly%20muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20and%20white%20coat%2C%20cartoon%20mascot%20style%2C%20cheerful%20expression%2C%20strong%20build%2C%20professional%20healthcare%20worker%2C%20anime%20character%20design%20with%20defined%20muscles%2C%20welcoming%20pose%2C%20clean%20simple%20background&width=60&height=60&seq=beast-kun-experience-elderly&orientation=squarish"
                  alt="ビースト君"
                  className="w-14 h-14 object-cover object-top rounded-full border-2 border-teal-300"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-teal-600 mb-4">ビースト君の現場での経験</h3>
                <div className="space-y-4 text-gray-700">
                  <p>
                    薬局で働いていると、高齢者の方々から「薬を飲んだかどうか分からない」という相談をよく受けます。
                    特に印象的だったのは、80代の男性患者さんのケースです。
                  </p>
                  <p>
                    その方は糖尿病、高血圧、心疾患で計8種類の薬を服用していましたが、飲み忘れが頻繁でした。
                    お薬カレンダーを提案し、ご家族と一緒に使い方を説明したところ、服薬コンプライアンスが大幅に改善しました。
                  </p>
                  <p>
                    大切なのは、患者さん一人ひとりの生活スタイルに合わせた管理方法を見つけることです。
                    完璧を求めすぎず、継続できる方法を選ぶことが成功の秘訣ですね！💪
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">まとめ</h2>
          
          <div className="bg-white border-2 border-teal-200 rounded-xl p-8">
            <div className="space-y-4 text-gray-700">
              <p>
                高齢者の服薬管理は、本人だけでなく家族や介護者も含めたチーム全体で取り組むことが重要です。
                お薬カレンダーや一包化サービスなどのツールを活用し、継続可能な管理方法を見つけましょう。
              </p>
              <p>
                飲み忘れや重複服用は、治療効果の低下や副作用のリスクを高めます。
                定期的な見直しと、医師・薬剤師との連携により、安全で効果的な薬物療法を継続できます。
              </p>
              <p>
                困ったことがあれば、遠慮なく薬剤師に相談してください。
                一人ひとりに最適な服薬管理方法を一緒に見つけていきましょう。
              </p>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">関連記事</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/beast-blog/constipation-medicine-guide" className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-medicine-bottle-line"></i>
                </div>
                <span className="text-sm text-gray-500">2025.10.09</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">便秘薬の正しい使い方と種類の違い</h3>
              <p className="text-gray-600">便秘薬の種類別の特徴と適切な使用方法を解説します。</p>
            </Link>
            
            <Link to="/beast-blog/medicine-not-working" className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-medicine-bottle-line"></i>
                </div>
                <span className="text-sm text-gray-500">2025.10.06</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">薬が効かないときの原因5選</h3>
              <p className="text-gray-600">薬の効果が感じられない時の原因と対処法を解説します。</p>
            </Link>
          </div>
        </section>

        {/* Back to Blog */}
        <div className="text-center">
          <Link 
            to="/beast-blog" 
            className="inline-flex items-center bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            ビースト君ブログ一覧に戻る
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職・派遣情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
